import { useEffect, useRef } from 'react';
import { LocalStorageKeys } from '../enums/EnumsValues';

/** Other tabs share credentials, but must not issue a second backend logout. */
export function useExternalSignOut(
  authenticated: boolean,
  currentUserId: number | undefined,
  onSignedOut: () => void,
  onAccountChanged: () => void,
) {
  const latest = useRef({ onSignedOut, onAccountChanged });
  latest.current = { onSignedOut, onAccountChanged };

  useEffect(() => {
    if (!authenticated) return;
    let synchronized = false;
    const initialToken = localStorage.getItem(LocalStorageKeys.Token);
    const checkSession = () => {
      if (synchronized) return;
      const raw = localStorage.getItem(LocalStorageKeys.Token);
      let token: unknown = raw;
      try {
        token = raw === null ? null : JSON.parse(raw);
      } catch {
        // Legacy plain token.
      }
      if (!token) {
        synchronized = true;
        latest.current.onSignedOut();
        return;
      }
      const storedUserId = Number(
        localStorage.getItem(LocalStorageKeys.UserId)?.replaceAll('"', ''),
      );
      if (
        currentUserId &&
        raw !== initialToken &&
        (!Number.isSafeInteger(storedUserId) || storedUserId !== currentUserId)
      ) {
        synchronized = true;
        latest.current.onAccountChanged();
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea && event.storageArea !== localStorage) return;
      if (
        event.key === null ||
        event.key === LocalStorageKeys.Token ||
        event.key === LocalStorageKeys.UserId
      ) {
        checkSession();
      }
    };
    const onVisible = () => {
      if (document.visibilityState !== 'hidden') checkSession();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', checkSession);
    document.addEventListener('visibilitychange', onVisible);
    // Covers a logout while the initial authentication request was in flight.
    checkSession();
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', checkSession);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authenticated, currentUserId]);
}
